import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import api from "shared/api/axios-instance";
import { T } from "../../../admin.theme";
import { categorizePhotos } from "../../model/types/document.types";
import { PhotoLightbox } from "./PhotoLightbox";
var docTypeLabel = {
    PASSPORT: "Паспорт",
    FRONT_BACK: "Документ (лицо / оборот)",
    ID_CARD: "ID-карта",
    UNKNOWN: "Неизвестный тип документа",
};
var PhotoThumb = function (_a) {
    var photo = _a.photo, label = _a.label, onClick = _a.onClick;
    var _b = useState(null), src = _b[0], setSrc = _b[1];
    var _c = useState(photo.documentsUrl ? "loading" : "error"), state = _c[0], setState = _c[1];
    var blobRef = useRef(null);
    useEffect(function () {
        if (!photo.documentsUrl)
            return;
        var cancelled = false;
        var img = new window.Image();
        img.onload = function () {
            if (cancelled)
                return;
            setSrc(photo.documentsUrl);
            setState("loaded");
        };
        img.onerror = function () {
            if (cancelled)
                return;
            api
                .get(photo.documentsUrl, { responseType: "blob" })
                .then(function (res) {
                if (cancelled)
                    return;
                var blob = URL.createObjectURL(res.data);
                blobRef.current = blob;
                setSrc(blob);
                setState("loaded");
            })
                .catch(function () {
                if (!cancelled)
                    setState("error");
            });
        };
        img.src = photo.documentsUrl;
        return function () {
            cancelled = true;
            img.onload = null;
            img.onerror = null;
            if (blobRef.current) {
                URL.revokeObjectURL(blobRef.current);
                blobRef.current = null;
            }
        };
    }, [photo.documentsUrl]);
    return (_jsxs(Box, { children: [_jsx(Typography, { variant: "caption", sx: { color: T.textMuted, display: "block", mb: 0.5, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.4px" }, children: label }), _jsxs(Box, { onClick: function () { return state === "loaded" && src && onClick(src); }, sx: {
                    width: 220,
                    height: 160,
                    borderRadius: "".concat(T.radiusSm, "px"),
                    overflow: "hidden",
                    border: "1px solid ".concat(T.border),
                    cursor: state === "loaded" ? "zoom-in" : "default",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: T.surface2,
                    position: "relative",
                    transition: "border-color 200ms, box-shadow 200ms",
                    "&:hover": state === "loaded" ? { borderColor: T.accentTintStrong, boxShadow: "0 0 0 2px ".concat(T.accentTint) } : {},
                }, children: [state === "loading" && _jsx(CircularProgress, { size: 22, sx: { color: T.accent, opacity: 0.6 } }), state === "error" && (_jsxs(Box, { display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, children: [_jsx(BrokenImageIcon, { sx: { color: T.textMuted, fontSize: 28 } }), _jsx(Typography, { variant: "caption", sx: { color: T.textMuted, fontSize: 10 }, children: "\u041D\u0435\u0442 \u0438\u0437\u043E\u0431\u0440\u0430\u0436\u0435\u043D\u0438\u044F" })] })), state === "loaded" && src && (_jsx("img", { src: src, alt: label, style: { width: "100%", height: "100%", objectFit: "cover" } }))] })] }));
};
export var DocumentPhotoGrid = function (_a) {
    var photos = _a.photos;
    var _b = useState(null), lightboxUrl = _b[0], setLightboxUrl = _b[1];
    var _c = categorizePhotos(photos), selfie = _c.selfie, docPhotos = _c.docPhotos, docType = _c.docType;
    return (_jsxs(Box, { children: [_jsx(Box, { mb: 2, children: _jsx(Chip, { label: docTypeLabel[docType], size: "small", sx: {
                        background: docType === "UNKNOWN" ? "rgba(251,191,36,0.12)" : T.accentTint,
                        color: docType === "UNKNOWN" ? "#fbbf24" : T.accent,
                        border: "1px solid ".concat(docType === "UNKNOWN" ? "rgba(251,191,36,0.3)" : T.accentTintStrong),
                        fontSize: 11,
                        fontWeight: 600,
                    } }) }), _jsxs(Box, { display: "flex", flexWrap: "wrap", gap: 2.5, children: [docPhotos.length === 0 && docType !== "UNKNOWN" && (_jsx(Typography, { variant: "body2", sx: { color: T.textSecondary }, children: "\u0424\u043E\u0442\u043E \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u043E\u0432 \u043D\u0435\u0442." })), docPhotos.map(function (photo, idx) {
                        var label = docType === "FRONT_BACK"
                            ? photo.photoType === "FRONT"
                                ? "Лицо"
                                : "Оборот"
                            : docType === "ID_CARD"
                                ? "ID-\u043A\u0430\u0440\u0442\u0430 ".concat(idx + 1)
                                : "Паспорт";
                        return _jsx(PhotoThumb, { photo: photo, label: label, onClick: setLightboxUrl }, photo.id);
                    }), selfie ? (_jsx(PhotoThumb, { photo: selfie, label: "\u0421\u0435\u043B\u0444\u0438 \u0441 \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u043E\u043C", onClick: setLightboxUrl })) : (_jsxs(Box, { children: [_jsx(Typography, { variant: "caption", sx: { color: T.textMuted, display: "block", mb: 0.5, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.4px" }, children: "\u0421\u0435\u043B\u0444\u0438 \u0441 \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u043E\u043C" }), _jsx(Box, { sx: {
                                    width: 220,
                                    height: 160,
                                    borderRadius: "".concat(T.radiusSm, "px"),
                                    border: "1px dashed ".concat(T.border),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: T.surface2,
                                }, children: _jsx(Typography, { variant: "caption", sx: { color: T.textMuted }, children: "\u041D\u0435 \u0437\u0430\u0433\u0440\u0443\u0436\u0435\u043D\u043E" }) })] }))] }), _jsx(PhotoLightbox, { url: lightboxUrl, onClose: function () { return setLightboxUrl(null); } })] }));
};
